/**
 * Header organization selector.
 *
 * Behavior contract (designs/admin-mfa-org-sessions.md + API contract):
 * - Org options come from the authenticated GET /organizations query
 *   (server already guarded by `organizations:read`; the response contains
 *   only orgs the caller can read).
 * - Selecting dispatches `switchOrganization` → POST /auth/sessions/org.
 *   The server authorizes; the client never trusts its own orgId for access.
 * - The currently-active org label comes from orgSessionSlice, bootstrapped
 *   from the verified session token's claims decoded FOR DISPLAY ONLY
 *   (never an auth decision) and updated from the server response after a
 *   successful switch.
 * - States: loading (list fetch), empty, error (list fetch failure), and a
 *   switching state per option + disabled popover while a switch is in
 *   flight. Old tokens/UI persist until the server succeeds (the thunk owns
 *   the atomic adoption + RTK Query cache reset).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Loader2, Building2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { useGetOrganizationsQuery } from '@/store/api/organizationsApi';
import { switchOrganization, clearOrgError } from '@/store/auth/orgSessionSlice';

const OPTION_CLASS =
  'flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-accent focus:bg-accent focus:outline-none';

export function OrgSelector() {
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const activeOrgId = useAppSelector((s) => s.orgSession.activeOrgId);
  const activeOrgName = useAppSelector((s) => s.orgSession.activeOrgName);
  const switching = useAppSelector((s) => s.orgSession.switching);
  const switchError = useAppSelector((s) => s.orgSession.error);

  const {
    data: orgs,
    isLoading,
    isError,
    refetch,
  } = useGetOrganizationsQuery(undefined, {
    // Don't hit the network until the user opens the popover.
    skip: !open,
  });

  const query = search.trim().toLowerCase();
  const visibleOrgs = useMemo(
    () => (orgs ?? []).filter((org) => org.name.toLowerCase().includes(query)),
    [orgs, query],
  );

  // Keep the keyboard cursor in range as the filtered list changes.
  useEffect(() => {
    setActiveIndex((i) => Math.min(i, Math.max(0, visibleOrgs.length - 1)));
  }, [visibleOrgs.length]);

  // Focus the search field whenever the popover opens.
  useEffect(() => {
    if (open) {
      setSearch('');
      setActiveIndex(0);
      // Radix mounts content async; focus on next tick.
      const t = setTimeout(() => searchRef.current?.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Allow the listbox to receive key events after search input typing.
  useEffect(() => {
    if (switching) setOpen(false);
  }, [switching]);

  const handleSelect = (orgId: string) => {
    if (switching || orgId === activeOrgId) return;
    // Old session stays live until the thunk's success branch adopts the new
    // pair and resets the RTK Query cache; pending/rejected is handled in the
    // slice (switching flag + error message, session untouched on failure).
    void dispatch(switchOrganization(orgId));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (visibleOrgs.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % visibleOrgs.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + visibleOrgs.length) % visibleOrgs.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const org = visibleOrgs[activeIndex];
      if (org) handleSelect(org._id);
    }
  };

  const label = switching
    ? 'Switching…'
    : (activeOrgName ?? (activeOrgId ? 'Organization' : 'No organization'));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="max-w-[200px] gap-1.5"
          aria-label="Switch organization"
          disabled={switching}
        >
          <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="truncate">{label}</span>
          {switching ? (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden />
          ) : (
            <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-2" onKeyDown={handleKeyDown}>
        <div className="flex flex-col gap-2">
          <Input
            ref={searchRef}
            placeholder="Search organizations…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search organizations"
            role="combobox"
            aria-expanded={open}
            aria-controls="org-listbox"
            aria-activedescendant={`org-option-${visibleOrgs[activeIndex]?._id ?? 'none'}`}
          />
          {switchError && (
            <div
              role="alert"
              className="flex items-start gap-1.5 rounded border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive"
            >
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              <span className="min-w-0 break-words">{switchError}</span>
              <button
                type="button"
                className="ml-auto shrink-0 underline"
                onClick={() => dispatch(clearOrgError())}
                aria-label="Dismiss organization switch error"
              >
                dismiss
              </button>
            </div>
          )}
          <div
            id="org-listbox"
            ref={listRef}
            role="listbox"
            aria-label="Organizations"
            className="max-h-64 overflow-y-auto"
          >
            {isLoading && (
              <div
                className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground"
                data-testid="org-list-loading"
              >
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Loading organizations…
              </div>
            )}
            {isError && !isLoading && (
              <div
                role="alert"
                className="flex flex-col items-center gap-2 py-6 text-sm text-muted-foreground"
                data-testid="org-list-error"
              >
                <AlertCircle className="h-4 w-4 text-destructive" aria-hidden />
                <span>Could not load organizations.</span>
                <Button variant="outline" size="sm" onClick={() => refetch()}>
                  Retry
                </Button>
              </div>
            )}
            {!isLoading && !isError && visibleOrgs.length === 0 && (
              <p
                className="py-6 text-center text-sm text-muted-foreground"
                data-testid="org-list-empty"
              >
                {orgs?.length ? 'No organizations match.' : 'No organizations available.'}
              </p>
            )}
            {!isLoading &&
              !isError &&
              visibleOrgs.map((org, idx) => {
                const isActive = org._id === activeOrgId;
                return (
                  <button
                    key={org._id}
                    type="button"
                    id={`org-option-${org._id}`}
                    role="option"
                    aria-selected={isActive}
                    disabled={switching}
                    data-active={isActive ? 'true' : undefined}
                    className={cn(
                      OPTION_CLASS,
                      activeIndex === idx && 'bg-accent',
                      switching && 'cursor-not-allowed opacity-60',
                    )}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onClick={() => handleSelect(org._id)}
                  >
                    <Check
                      className={cn('h-4 w-4 shrink-0', isActive ? 'opacity-100' : 'opacity-0')}
                      aria-hidden
                    />
                    <span className="truncate">{org.name}</span>
                    {switching && isActive && (
                      <Loader2 className="ml-auto h-3.5 w-3.5 animate-spin" aria-hidden />
                    )}
                  </button>
                );
              })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
